import { useEffect, useState } from 'react';

// Analytics/advertising tags load ONLY after consent (UK GDPR / PECR).
// index.html reads the same key on load and defines window.__loadTrackers();
// until then window.gtag / window.fbq don't exist and the tracking helpers no-op.
const CONSENT_KEY = 'sc_cookie_consent';
const OPEN_EVENT = 'cookie-consent:open';
const TRACKING_COOKIES = /^(_ga|_gid|_gat|_gcl_|_fbp|_fbc|_clck|_clsk|CLID|MUID)/;

type Choice = 'accepted' | 'rejected';

declare global {
  interface Window {
    __loadTrackers?: () => void;
  }
}

function readChoice(): Choice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'accepted' || v === 'rejected' ? v : null;
  } catch {
    return null;
  }
}

function clearTrackingCookies() {
  const host = window.location.hostname;
  const domains = ['', host, `.${host.replace(/^www\./, '')}`];
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (!TRACKING_COOKIES.test(name)) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
    });
  });
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!readChoice()) setOpen(true);
    const show = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);

  const choose = (choice: Choice) => {
    const previous = readChoice();
    try {
      localStorage.setItem(CONSENT_KEY, choice);
    } catch {
      /* storage blocked: the banner will ask again next visit */
    }
    setOpen(false);
    if (choice === 'accepted') {
      window.__loadTrackers?.();
    } else if (previous === 'accepted') {
      clearTrackingCookies();
      window.location.reload();
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-gray-200 bg-white px-4 py-5 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] sm:px-6"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-gray-700 md:max-w-2xl">
          We&apos;d like to use analytics and advertising cookies (Google, Meta and Microsoft Clarity) to understand how the site is used and
          measure our adverts. They are only set if you accept.{' '}
          <a href="/cookie-policy" className="underline underline-offset-2">
            Cookie Policy
          </a>
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={() => choose('rejected')}
            className="flex-1 rounded-lg border border-gray-400 px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-100 md:flex-none"
          >
            Reject all
          </button>
          <button
            type="button"
            onClick={() => choose('accepted')}
            className="flex-1 rounded-lg border border-gray-900 bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-700 md:flex-none"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
